import { useAction, useMutation } from "convex/react"
import { useCallback, useEffect, useRef, useState } from "react"
import { createPortal } from "react-dom"
import { toast } from "sonner"
import { useSolanaSign } from "@/hooks/useSolanaSign"
import { useWebBalance } from "@/hooks/useWebBalance"
import { getErrorMessage } from "@/lib/errors"
import { api } from "~convex/_generated/api"
import { MAX_POST_LENGTH, MICRO_USDC } from "~shared/constants"
import { Spinner } from "./Spinner"

function formatMarketCap(mcap: number): string {
	if (mcap >= 1e9) {
		return `${(mcap / 1e9).toFixed(2)}B`
	}
	if (mcap >= 1e6) {
		return `${(mcap / 1e6).toFixed(2)}M`
	}
	if (mcap >= 1e3) {
		return `${(mcap / 1e3).toFixed(1)}K`
	}
	return mcap.toFixed(0)
}

interface CashtagBuyModalProps {
	onClose: () => void
	ticker: string
	/** When provided, skips ticker resolution and uses this mint directly */
	tokenMint?: string
	/** Pre-fill the amount input with this USDC value (e.g. 5 for $5). */
	defaultAmount?: number
	/** When true, fires the swap automatically once token info + amount are ready. */
	autoExecute?: boolean
}

export function CashtagBuyModal({
	onClose,
	ticker,
	tokenMint: propMint,
	defaultAmount,
	autoExecute,
}: CashtagBuyModalProps) {
	const [amount, setAmount] = useState(
		defaultAmount && defaultAmount > 0 ? String(defaultAmount) : "",
	)
	const [estimate, setEstimate] = useState<number | null>(null)
	const [tokenDecimals, setTokenDecimals] = useState(6)
	const [tokenName, setTokenName] = useState<string | null>(null)
	const [tokenMint, setTokenMint] = useState<string | null>(null)
	const [tokenIcon, setTokenIcon] = useState<string | null>(null)
	const [tokenMarketCap, setTokenMarketCap] = useState<number | null>(null)
	const [tokenPriceChange, setTokenPriceChange] = useState<number | null>(null)
	const [quoting, setQuoting] = useState(false)
	const [submitting, setSubmitting] = useState(false)
	const [successInfo, setSuccessInfo] = useState<{
		tokens: string
		swapTx: string
	} | null>(null)
	const [composeContent, setComposeContent] = useState("")
	const [posting, setPosting] = useState(false)
	const submittingRef = useRef(false)
	const autoExecutedRef = useRef(false)
	const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
	const mountedRef = useRef(true)

	const { usdcBalance, refetchUSDC } = useWebBalance()
	const { isReady: isWalletReady, signTransaction } = useSolanaSign()
	const fetchTokenInfo = useAction(api.swapActions.getTokenInfo)
	const fetchTokenInfoByMint = useAction(api.swapActions.getTokenInfoByMint)
	const fetchQuoteAction = useAction(api.swapActions.swapBuyTokenQuote)
	const prepareBuy = useAction(api.swapPrepare.prepareBuyToken)
	const commitBuy = useAction(api.swapPrepare.commitBuyToken)
	const createPostAfterBuy = useMutation(api.freeActions.createPostAfterBuy)

	useEffect(() => {
		mountedRef.current = true
		return () => {
			mountedRef.current = false
		}
	}, [])

	// Fetch token info immediately on mount
	useEffect(() => {
		const infoPromise = propMint
			? fetchTokenInfoByMint({ mint: propMint })
			: fetchTokenInfo({ ticker })
		infoPromise
			.then(info => {
				if (!mountedRef.current || !info) {
					return
				}
				setTokenName(info.name)
				setTokenMint(propMint ?? ("mint" in info ? (info.mint as string) : null))
				setTokenDecimals(info.decimals)
				setTokenIcon(info.icon)
				setTokenMarketCap(info.market_cap)
				setTokenPriceChange(info.price_change_24h)
			})
			.catch(() => {})
	}, [ticker, propMint, fetchTokenInfo, fetchTokenInfoByMint])

	// Pre-fill quote when defaultAmount is provided on mount
	// biome-ignore lint/correctness/useExhaustiveDependencies: only on mount
	useEffect(() => {
		if (!defaultAmount || defaultAmount <= 0) {
			return
		}
		const units = Math.floor(defaultAmount * MICRO_USDC)
		if (units >= MICRO_USDC) {
			fetchQuote(units).catch(() => {})
		}
	}, [])

	const parsedAmount = Number.parseFloat(amount) || 0
	const baseUnits = Math.floor(parsedAmount * MICRO_USDC)
	const maxAmount = usdcBalance / MICRO_USDC
	const isValidAmount = baseUnits >= MICRO_USDC && baseUnits <= usdcBalance
	const canSubmit = isValidAmount && isWalletReady && !submitting && !quoting

	const fetchQuote = useCallback(
		async (units: number) => {
			if (!mountedRef.current) {
				return
			}
			setQuoting(true)
			try {
				const result = await fetchQuoteAction({ ticker, usdc_micro: units, mint: propMint })
				if (mountedRef.current) {
					setEstimate(result.estimated_tokens)
					setTokenDecimals(result.token_info.decimals)
				}
			} catch {
				if (mountedRef.current) {
					setEstimate(null)
				}
			} finally {
				if (mountedRef.current) {
					setQuoting(false)
				}
			}
		},
		[ticker, propMint, fetchQuoteAction],
	)

	const handleAmountChange = useCallback(
		(text: string) => {
			setAmount(text)
			setEstimate(null)

			if (debounceRef.current) {
				clearTimeout(debounceRef.current)
			}

			const parsed = Number.parseFloat(text) || 0
			const units = Math.floor(parsed * MICRO_USDC)
			if (units < MICRO_USDC || units > usdcBalance) {
				setQuoting(false)
				return
			}

			debounceRef.current = setTimeout(() => {
				fetchQuote(units).catch(() => {})
			}, 500)
		},
		[usdcBalance, fetchQuote],
	)

	const handleMax = useCallback(() => {
		if (maxAmount >= 1) {
			const str = (Math.floor(usdcBalance / 10_000) / 100).toFixed(2)
			handleAmountChange(str)
		}
	}, [usdcBalance, maxAmount, handleAmountChange])

	const executeSwap = useCallback(async () => {
		if (submittingRef.current || baseUnits <= 0) {
			return
		}
		submittingRef.current = true
		setSubmitting(true)

		try {
			const prepared = await prepareBuy({ ticker, usdc_micro: baseUnits, mint: propMint })
			const signedSwapTx = await signTransaction(prepared.swapTx)
			const result = await commitBuy({
				prepare_token: prepared.prepareToken,
				signed_swap_tx: signedSwapTx,
			})
			const divisor = 10 ** (prepared.tokenInfo.decimals || 6)
			const tokens = (result.estimated_tokens / divisor).toLocaleString(undefined, {
				maximumFractionDigits: 4,
			})
			refetchUSDC()
			setAmount("")
			setSuccessInfo({ swapTx: result.swap_tx, tokens })
		} catch (e: unknown) {
			toast.error(getErrorMessage(e))
		} finally {
			submittingRef.current = false
			setSubmitting(false)
		}
	}, [baseUnits, ticker, propMint, prepareBuy, commitBuy, signTransaction, refetchUSDC])

	// Auto-execute the buy once token info + amount are ready (instant-buy flow)
	useEffect(() => {
		if (
			!autoExecute ||
			autoExecutedRef.current ||
			!tokenMint ||
			!isWalletReady ||
			!isValidAmount ||
			submitting ||
			successInfo
		) {
			return
		}
		autoExecutedRef.current = true
		executeSwap().catch(() => {})
	}, [autoExecute, tokenMint, isWalletReady, isValidAmount, submitting, successInfo, executeSwap])

	const handleConfirm = useCallback(() => {
		if (!canSubmit || submittingRef.current) {
			return
		}
		executeSwap().catch(() => {})
	}, [canSubmit, executeSwap])

	const handleSharePost = useCallback(async () => {
		const trimmed = composeContent.trim()
		if (!successInfo || !tokenMint || !tokenName || posting || trimmed.length === 0) {
			return
		}
		setPosting(true)
		try {
			await createPostAfterBuy({
				content: trimmed,
				community_mint: tokenMint,
				community_name: tokenName,
				community_ticker: ticker,
				swap_tx_signature: successInfo.swapTx,
			})
			toast.success("Posted")
			setSuccessInfo(null)
			setComposeContent("")
			onClose()
		} catch (e: unknown) {
			toast.error(getErrorMessage(e))
		} finally {
			setPosting(false)
		}
	}, [
		successInfo,
		tokenMint,
		tokenName,
		ticker,
		composeContent,
		posting,
		createPostAfterBuy,
		onClose,
	])

	const divisor = 10 ** tokenDecimals
	const estimateLabel =
		estimate !== null
			? `~${(estimate / divisor).toLocaleString(undefined, { maximumFractionDigits: 4 })} $${ticker}`
			: null

	return createPortal(
		<dialog
			aria-label={`Buy $${ticker}`}
			className="fixed inset-0 z-50 flex h-full max-h-full w-full max-w-full animate-fade-in items-center justify-center border-0 bg-black/70 open:flex"
			onClick={onClose}
			onKeyDown={e => {
				if (e.key === "Escape") {
					onClose()
				}
			}}
			open
		>
			{/* biome-ignore lint/a11y/noStaticElementInteractions: prevents backdrop click-through */}
			{/* biome-ignore lint/a11y/useKeyWithClickEvents: keyboard handled by dialog parent */}
			<div
				className="w-[95vw] max-w-sm animate-slide-up rounded-2xl border border-app-border bg-app-surface p-6 shadow-2xl"
				onClick={e => e.stopPropagation()}
			>
				<div className="mb-4 flex items-start gap-3">
					{tokenIcon ? (
						<img alt="" className="h-11 w-11 rounded-full" src={tokenIcon} />
					) : (
						<div className="h-11 w-11 rounded-full bg-app-surface-light" />
					)}
					<div className="flex-1">
						<div className="flex items-start justify-between">
							<div>
								{tokenName && (
									<p className="font-semibold text-app-text text-sm leading-tight">{tokenName}</p>
								)}
								<div className="flex items-center gap-1 text-app-text-secondary text-sm">
									<span>${ticker}</span>
									{tokenMint && (
										<>
											<span className="text-app-text-tertiary text-xs">
												{tokenMint.slice(0, 4)}...{tokenMint.slice(-4)}
											</span>
											<button
												className="text-app-text-tertiary transition-colors hover:text-app-text-secondary"
												onClick={() => {
													navigator.clipboard.writeText(tokenMint).catch(() => {})
													toast.success("CA copied")
												}}
												type="button"
											>
												<svg
													className="h-3 w-3"
													fill="none"
													stroke="currentColor"
													viewBox="0 0 24 24"
												>
													<rect height={13} rx={2} strokeWidth={1.5} width={13} x={9} y={9} />
													<path
														d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"
														strokeWidth={1.5}
													/>
												</svg>
											</button>
										</>
									)}
								</div>
							</div>
							<div className="text-right">
								{tokenMarketCap != null && tokenMarketCap > 0 && (
									<p className="font-semibold text-app-text text-sm">
										${formatMarketCap(tokenMarketCap)}
									</p>
								)}
								{tokenPriceChange != null && (
									<p
										className={`font-semibold text-sm ${tokenPriceChange >= 0 ? "text-app-green" : "text-app-red"}`}
									>
										{tokenPriceChange >= 0 ? "+" : ""}
										{tokenPriceChange.toFixed(1)}%
									</p>
								)}
							</div>
						</div>
					</div>
				</div>

				{successInfo ? (
					<div className="py-2">
						<p className="mb-1 text-center font-bold text-app-text text-lg">
							Bought {successInfo.tokens} ${ticker}
						</p>
						<p className="mb-3 text-center text-app-text-secondary text-sm">Share your take</p>
						<textarea
							autoFocus
							className="mb-4 min-h-[80px] w-full resize-none rounded-xl border border-app-border bg-app-surface-light px-3 py-3 text-app-text outline-none placeholder:text-app-text-tertiary"
							disabled={posting}
							maxLength={MAX_POST_LENGTH}
							onChange={e => setComposeContent(e.target.value)}
							placeholder={`What's your take on $${ticker}?`}
							value={composeContent}
						/>
						<div className="flex gap-3">
							<button
								className="flex-1 rounded-full border border-app-border py-2.5 font-semibold text-app-text-secondary text-sm transition-colors hover:border-app-text-secondary hover:text-app-text disabled:cursor-not-allowed disabled:opacity-40"
								disabled={posting}
								onClick={onClose}
								type="button"
							>
								Skip
							</button>
							<button
								className="flex-1 rounded-full bg-app-green py-2.5 font-bold text-base text-black transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40"
								disabled={posting || composeContent.trim().length === 0}
								onClick={() => {
									handleSharePost().catch(() => {})
								}}
								type="button"
							>
								{posting ? <Spinner size="sm" /> : "Post"}
							</button>
						</div>
					</div>
				) : (
					<>
						<div className="mb-2 flex items-center gap-2 rounded-xl border border-app-border bg-app-surface-light px-4 py-3">
							<span className="font-bold text-2xl text-app-text-secondary">$</span>
							<input
								autoFocus
								className="min-w-0 flex-1 bg-transparent font-bold text-2xl text-app-text outline-none placeholder:text-app-text-tertiary"
								disabled={submitting}
								inputMode="decimal"
								onChange={e => handleAmountChange(e.target.value)}
								placeholder="0.00"
								type="text"
								value={amount}
							/>
							<button
								className="font-semibold text-app-green text-sm transition-colors hover:text-app-green/80"
								disabled={submitting}
								onClick={handleMax}
								type="button"
							>
								Max
							</button>
						</div>

						<div className="mb-4 text-right">
							<button
								className="text-app-text-tertiary text-xs transition-colors hover:text-app-text"
								onClick={handleMax}
								type="button"
							>
								Available: ${maxAmount.toFixed(2)} USDC
							</button>
						</div>

						{parsedAmount > 0 && parsedAmount < 1 && (
							<p className="mb-3 text-center text-red-400 text-sm">Minimum $1.00</p>
						)}
						{baseUnits > usdcBalance && parsedAmount >= 1 && (
							<p className="mb-3 text-center text-red-400 text-sm">Insufficient balance</p>
						)}

						{quoting && (
							<div className="mb-3 flex items-center justify-center gap-2 text-app-text-secondary text-sm">
								<Spinner size="sm" />
								<span>Getting quote...</span>
							</div>
						)}
						{estimateLabel && !quoting && (
							<div className="mb-3 flex items-center justify-between text-sm">
								<span className="text-app-text-secondary">You'll receive</span>
								<span className="font-semibold text-app-green">{estimateLabel}</span>
							</div>
						)}

						<button
							className={`w-full rounded-full bg-app-green py-2.5 font-bold text-base text-black transition-opacity ${
								canSubmit ? "hover:opacity-90" : "cursor-not-allowed opacity-40"
							}`}
							disabled={!canSubmit}
							onClick={handleConfirm}
							type="button"
						>
							{submitting ? (
								<Spinner />
							) : (
								`Buy $${parsedAmount >= 1 ? parsedAmount.toFixed(2) : "0.00"} of $${ticker}`
							)}
						</button>

						<button
							className="mt-3 w-full py-2 text-app-text-tertiary text-sm transition-colors hover:text-app-text"
							onClick={onClose}
							type="button"
						>
							Cancel
						</button>
					</>
				)}
			</div>
		</dialog>,
		document.body,
	)
}
